import { useEffect, useRef, useState } from 'react';
import { Plus, Edit, Trash2, Save, X, Upload, Download, Gift } from 'lucide-react';
import { addDoc, collection, deleteDoc, doc, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { uploadImage } from '../services/imageUploadService';

interface DigitalSouvenir {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  location: string;
  category: string;
  coverImage: string;
  downloadUrl: string;
  tags: string[];
  isActive: boolean;
  emailSubject?: string;
}

const EMPTY_FORM: Omit<DigitalSouvenir, 'id'> = {
  title: '',
  subtitle: '',
  description: '',
  location: '',
  category: 'Luoghi',
  coverImage: '',
  downloadUrl: '',
  tags: [],
  isActive: true,
  emailSubject: ''
};

const categories = ['Luoghi', 'Eventi', 'Servizi', 'Storie', 'Merchandising'];

const SouvenirsNew = () => {
  const [souvenirs, setSouvenirs] = useState<DigitalSouvenir[]>([]);
  const [selectedSouvenir, setSelectedSouvenir] = useState<DigitalSouvenir | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [tagsInput, setTagsInput] = useState('');

  const [formData, setFormData] = useState<Omit<DigitalSouvenir, 'id'>>(EMPTY_FORM);

  const coverInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadSouvenirs();
  }, []);

  const loadSouvenirs = async () => {
    try {
      setIsLoading(true);
      const snapshot = await getDocs(collection(db, 'digitalSouvenirs'));
      const data = snapshot.docs
        .map((snapshotDoc) => ({ id: snapshotDoc.id, ...snapshotDoc.data() }))
        .map((item) => {
          const mapped = item as DigitalSouvenir;
          return {
            ...mapped,
            subtitle: mapped.subtitle || '',
            tags: Array.isArray(mapped.tags) ? mapped.tags : [],
            emailSubject: mapped.emailSubject || ''
          };
        })
        .sort((a, b) => (a.title || '').localeCompare(b.title || '', 'it', { sensitivity: 'base' }));

      setSouvenirs(data);
      if (data.length > 0) {
        handleSelectSouvenir(data[0]);
      }
    } catch (error) {
      console.error('Errore caricamento souvenir digitali:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectSouvenir = (souvenir: DigitalSouvenir) => {
    setSelectedSouvenir(souvenir);
    setFormData({
      title: souvenir.title || '',
      subtitle: souvenir.subtitle || '',
      description: souvenir.description || '',
      location: souvenir.location || '',
      category: souvenir.category || 'Luoghi',
      coverImage: souvenir.coverImage || '',
      downloadUrl: souvenir.downloadUrl || '',
      tags: souvenir.tags || [],
      isActive: souvenir.isActive !== false,
      emailSubject: souvenir.emailSubject || ''
    });
    setTagsInput((souvenir.tags || []).join(', '));
    setIsEditing(false);
  };

  const handleNew = () => {
    setSelectedSouvenir(null);
    setFormData(EMPTY_FORM);
    setTagsInput('');
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (selectedSouvenir) {
      handleSelectSouvenir(selectedSouvenir);
    } else {
      setFormData(EMPTY_FORM);
      setTagsInput('');
      setIsEditing(false);
    }
  };

  const parseTags = (raw: string) =>
    raw
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean)
      .filter((tag, idx, arr) => arr.indexOf(tag) === idx);

  const handleSave = async () => {
    try {
      const payload: Omit<DigitalSouvenir, 'id'> = {
        ...formData,
        tags: parseTags(tagsInput),
        title: formData.title.trim(),
        subtitle: formData.subtitle.trim(),
        description: formData.description.trim(),
        location: formData.location.trim(),
        emailSubject: (formData.emailSubject || '').trim()
      };

      if (!payload.title) {
        alert('Inserisci almeno un titolo per il souvenir.');
        return;
      }

      if (!payload.coverImage) {
        alert('Carica o inserisci una cover per il souvenir.');
        return;
      }

      if (selectedSouvenir) {
        await updateDoc(doc(db, 'digitalSouvenirs', selectedSouvenir.id), payload);
        const updated = { ...selectedSouvenir, ...payload };
        setSouvenirs((prev) =>
          prev
            .map((item) => (item.id === selectedSouvenir.id ? updated : item))
            .sort((a, b) => (a.title || '').localeCompare(b.title || '', 'it', { sensitivity: 'base' }))
        );
        setSelectedSouvenir(updated);
      } else {
        const created = await addDoc(collection(db, 'digitalSouvenirs'), payload);
        const newSouvenir: DigitalSouvenir = { id: created.id, ...payload };
        setSouvenirs((prev) =>
          [...prev, newSouvenir].sort((a, b) =>
            (a.title || '').localeCompare(b.title || '', 'it', { sensitivity: 'base' })
          )
        );
        setSelectedSouvenir(newSouvenir);
      }

      setIsEditing(false);
    } catch (error) {
      console.error('Errore salvataggio souvenir:', error);
      alert('Errore durante il salvataggio.');
    }
  };

  const handleDelete = async (souvenirId: string) => {
    if (!window.confirm('Vuoi eliminare questo souvenir digitale?')) return;

    try {
      await deleteDoc(doc(db, 'digitalSouvenirs', souvenirId));
      const next = souvenirs.filter((item) => item.id !== souvenirId);
      setSouvenirs(next);
      if (selectedSouvenir?.id === souvenirId) {
        setSelectedSouvenir(next[0] || null);
        if (next[0]) {
          handleSelectSouvenir(next[0]);
        } else {
          setFormData(EMPTY_FORM);
          setTagsInput('');
          setIsEditing(false);
        }
      }
    } catch (error) {
      console.error('Errore eliminazione souvenir:', error);
      alert('Errore durante l\'eliminazione.');
    }
  };

  const uploadCover = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingCover(true);
      const souvenirId = selectedSouvenir?.id || `temp_${Date.now()}`;
      const extension = file.name.split('.').pop() || 'jpg';
      const path = `souvenirs/${souvenirId}/cover/${Date.now()}.${extension}`;
      const url = await uploadImage(file, path);
      setFormData((prev) => ({ ...prev, coverImage: url }));
    } catch (error) {
      console.error('Errore upload cover souvenir:', error);
      alert('Errore durante il caricamento della cover.');
    } finally {
      setIsUploadingCover(false);
      event.target.value = '';
    }
  };

  const uploadDownloadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingFile(true);
      const souvenirId = selectedSouvenir?.id || `temp_${Date.now()}`;
      const extension = file.name.split('.').pop() || 'bin';
      const path = `souvenirs/${souvenirId}/downloads/${Date.now()}.${extension}`;
      const url = await uploadImage(file, path);
      setFormData((prev) => ({ ...prev, downloadUrl: url }));
    } catch (error) {
      console.error('Errore upload file souvenir:', error);
      alert('Errore durante il caricamento del file.');
    } finally {
      setIsUploadingFile(false);
      event.target.value = '';
    }
  };

  const activeCount = souvenirs.filter((item) => item.isActive !== false).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-primary text-white p-6">
        <div className="container-max">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Gift size={24} />
              <h1 className="text-2xl font-bold">Souvenir Digitali</h1>
            </div>
            <button
              onClick={handleNew}
              className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
            >
              <Plus size={16} />
              <span>Nuovo Souvenir</span>
            </button>
          </div>
          <p className="text-white/85 mt-2">
            Carica cartoline digitali e contenuti scaricabili da personalizzare nella mobile app.
          </p>
        </div>
      </div>

      <div className="container-max p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-border">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="text-lg font-semibold text-text-primary">Catalogo</h2>
                <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                  {activeCount}/{souvenirs.length} attivi
                </span>
              </div>

              <div className="max-h-[640px] overflow-y-auto">
                {isLoading ? (
                  <div className="p-4 text-sm text-text-secondary">Caricamento...</div>
                ) : souvenirs.length === 0 ? (
                  <div className="p-4 text-sm text-text-secondary">Nessun souvenir disponibile.</div>
                ) : (
                  souvenirs.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleSelectSouvenir(item)}
                      className={`w-full text-left p-4 border-b border-border transition-colors duration-200 ${
                        selectedSouvenir?.id === item.id ? 'bg-primary/5' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-text-primary leading-snug">{item.title}</p>
                          <p className="text-xs text-text-secondary mt-1">{item.location || 'Posizione non indicata'}</p>
                        </div>
                        <span
                          className={`text-[10px] px-2 py-1 rounded-full ${
                            item.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {item.isActive ? 'Attivo' : 'Bozza'}
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-border">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="text-lg font-semibold text-text-primary">
                  {selectedSouvenir ? 'Dettaglio Souvenir' : 'Nuovo Souvenir'}
                </h2>
                {!isEditing ? (
                  <div className="flex items-center space-x-2">
                    {selectedSouvenir && (
                      <>
                        <button
                          onClick={() => setIsEditing(true)}
                          className="px-3 py-2 text-sm rounded-lg border border-border hover:bg-gray-50 flex items-center space-x-2"
                        >
                          <Edit size={14} />
                          <span>Modifica</span>
                        </button>
                        <button
                          onClick={() => handleDelete(selectedSouvenir.id)}
                          className="px-3 py-2 text-sm rounded-lg border border-error/40 text-error hover:bg-error/5 flex items-center space-x-2"
                        >
                          <Trash2 size={14} />
                          <span>Elimina</span>
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleCancel}
                      className="px-3 py-2 text-sm rounded-lg border border-border hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <X size={14} />
                      <span>Annulla</span>
                    </button>
                    <button
                      onClick={handleSave}
                      className="px-3 py-2 text-sm rounded-lg bg-primary text-white hover:bg-primary/90 flex items-center space-x-2"
                    >
                      <Save size={14} />
                      <span>Salva</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="p-6 space-y-6">
                {isEditing ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Titolo</label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          className="w-full px-3 py-2 border border-border rounded-lg"
                          placeholder="Es. Cartolina della Marina"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Sottotitolo</label>
                        <input
                          type="text"
                          value={formData.subtitle}
                          onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                          className="w-full px-3 py-2 border border-border rounded-lg"
                          placeholder="Es. Luce dorata sul porto"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Luogo associato</label>
                        <input
                          type="text"
                          value={formData.location}
                          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          className="w-full px-3 py-2 border border-border rounded-lg"
                          placeholder="Es. Via Giordano"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Categoria</label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          className="w-full px-3 py-2 border border-border rounded-lg"
                        >
                          {categories.map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Descrizione</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={4}
                        className="w-full px-3 py-2 border border-border rounded-lg"
                        placeholder="Descrizione narrativa della cartolina digitale..."
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium">Cover cartolina</label>
                        <input
                          type="text"
                          value={formData.coverImage}
                          onChange={(e) => setFormData({ ...formData, coverImage: e.target.value })}
                          className="w-full px-3 py-2 border border-border rounded-lg"
                          placeholder="https://..."
                        />
                        <button
                          type="button"
                          onClick={() => coverInputRef.current?.click()}
                          className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 flex items-center space-x-2"
                          disabled={isUploadingCover}
                        >
                          <Upload size={14} />
                          <span>{isUploadingCover ? 'Upload in corso...' : 'Carica Cover'}</span>
                        </button>
                        <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={uploadCover} />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium">File scaricabile (immagine/PDF)</label>
                        <input
                          type="text"
                          value={formData.downloadUrl}
                          onChange={(e) => setFormData({ ...formData, downloadUrl: e.target.value })}
                          className="w-full px-3 py-2 border border-border rounded-lg"
                          placeholder="https://..."
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 flex items-center space-x-2"
                          disabled={isUploadingFile}
                        >
                          <Upload size={14} />
                          <span>{isUploadingFile ? 'Upload in corso...' : 'Carica File'}</span>
                        </button>
                        <input ref={fileInputRef} type="file" className="hidden" onChange={uploadDownloadFile} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Tag (separati da virgola)</label>
                      <input
                        type="text"
                        value={tagsInput}
                        onChange={(e) => setTagsInput(e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-lg"
                        placeholder="es. marina, tramonto, souvenir"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Template oggetto email (opzionale)</label>
                      <input
                        type="text"
                        value={formData.emailSubject || ''}
                        onChange={(e) => setFormData({ ...formData, emailSubject: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-lg"
                        placeholder="Ti invio una cartolina da Casal Velino"
                      />
                    </div>

                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                      <span>Souvenir attivo in app</span>
                    </label>
                  </>
                ) : selectedSouvenir ? (
                  <div className="space-y-5">
                    <div className="relative rounded-xl overflow-hidden border border-border">
                      <img
                        src={selectedSouvenir.coverImage || '/placeholder-trail.jpg'}
                        alt={selectedSouvenir.title}
                        className="w-full h-64 object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent p-4 flex flex-col justify-end">
                        <p className="text-white text-2xl font-bold">{selectedSouvenir.title}</p>
                        {!!selectedSouvenir.subtitle && (
                          <p className="text-white/90 text-sm mt-1">{selectedSouvenir.subtitle}</p>
                        )}
                        <p className="text-white/80 text-xs mt-2">{selectedSouvenir.location}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg bg-gray-50 border border-border">
                        <p className="text-xs uppercase tracking-wide text-text-secondary">Categoria</p>
                        <p className="font-semibold text-text-primary mt-1">{selectedSouvenir.category}</p>
                      </div>
                      <div className="p-4 rounded-lg bg-gray-50 border border-border">
                        <p className="text-xs uppercase tracking-wide text-text-secondary">Stato</p>
                        <p className="font-semibold text-text-primary mt-1">
                          {selectedSouvenir.isActive ? 'Attivo in app' : 'Bozza'}
                        </p>
                      </div>
                    </div>

                    <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-wrap">
                      {selectedSouvenir.description || 'Nessuna descrizione inserita.'}
                    </p>

                    {!!selectedSouvenir.tags?.length && (
                      <div className="flex flex-wrap gap-2">
                        {selectedSouvenir.tags.map((tag) => (
                          <span key={tag} className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-3">
                      {selectedSouvenir.downloadUrl && (
                        <a
                          href={selectedSouvenir.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 inline-flex items-center gap-2"
                        >
                          <Download size={14} />
                          <span>Apri File Scaricabile</span>
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-text-secondary">Seleziona un souvenir o creane uno nuovo.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SouvenirsNew;
